import { useEffect, useState } from "react";
import { api, getToken, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { STATUS_META, fmtEuro, isInternal } from "@/lib/constants";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TrendingUp, Wallet, Activity, CheckCircle2, Download, BarChart3 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, CartesianGrid, ComposedChart, Legend, Area } from "recharts";
import { ExportDialog, DATE_FIELDS_PRATICHE, PRACTICE_EXPORT_FIELDS } from "@/components/ExportDialog";
import { useExports } from "@/context/ExportsContext";
import { MultiSelect } from "@/components/MultiSelect";
import { toast } from "sonner";

const MONTHS = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const CUR_YEAR = String(new Date().getFullYear());

const mergeByName = (cur = [], prev = []) => {
  const pm = Object.fromEntries(prev.map((x) => [x.name, x.count]));
  const rows = cur.map((x) => ({ ...x, count_prev: pm[x.name] || 0 }));
  prev.filter((x) => !cur.some((c) => c.name === x.name)).forEach((x) => rows.push({ name: x.name, count: 0, count_prev: x.count }));
  return rows;
};

const monthlyByIndex = (monthly = []) => {
  const out = Array.from({ length: 12 }, () => ({ count: 0, volume: 0 }));
  monthly.forEach((m) => { const i = Number(String(m.month).slice(5, 7)) - 1; if (i >= 0 && i < 12) out[i] = m; });
  return out;
};

function Kpi({ icon: Icon, label, value, sub, testid, tint }) {
  return (
    <Card className="p-5 flex items-start justify-between" data-testid={testid}>
      <div>
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
        <p className="font-heading text-2xl font-bold mt-1.5">{value}</p>
        {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
      </div>
      <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${tint}`}><Icon className="h-5 w-5" /></div>
    </Card>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [expOpen, setExpOpen] = useState(false);
  const [expType, setExpType] = useState("report");
  const [provv, setProvv] = useState(null);
  const [year, setYear] = useState(CUR_YEAR);
  const [month, setMonth] = useState("all");
  const [prodotto, setProdotto] = useState("all");
  const [products, setProducts] = useState([]);
  useEffect(() => { api.get("/meta").then((r) => setProducts(r.data.products || [])).catch(() => {}); }, []);
  const [prev, setPrev] = useState(null);
  const [prevProvv, setPrevProvv] = useState(null);
  const prevYear = year === "all" ? null : String(Number(year) - 1);

  useEffect(() => {
    const params = {};
    if (year !== "all") params.year = year;
    if (month !== "all") params.month = month;
    if (prodotto !== "all") params.prodotto = prodotto;
    api.get("/analytics", { params }).then((r) => setData(r.data)).catch(() => { setData(null); toast.error("Errore nel caricamento della dashboard"); });
    api.get("/analytics/provvigioni", { params }).then((r) => setProvv(r.data)).catch(() => {});
    setPrev(null); setPrevProvv(null);
    if (year !== "all") {
      const pp = { ...params, year: String(Number(year) - 1) };
      api.get("/analytics", { params: pp }).then((r) => setPrev(r.data)).catch(() => {});
      api.get("/analytics/provvigioni", { params: pp }).then((r) => setPrevProvv(r.data)).catch(() => {});
    }
  }, [year, month, prodotto]);

  const { startExport } = useExports();
  const onExport = (params) => {
    if (expType === "sede") startExport("reports_per_sede", params, "Report per Sede");
    else startExport("reports_excel", params, "Report Pratiche");
  };

  if (!data) return <div className="text-muted-foreground">Caricamento dashboard…</div>;

  const statusChart = data.by_status.filter((s) => s.count > 0);
  let monthChart = data.monthly.map((m) => ({ ...m, volK: Math.round(m.volume / 1000) }));
  if (year !== "all") {
    const cur = monthlyByIndex(data.monthly);
    const pv = monthlyByIndex(prev?.monthly);
    monthChart = cur.map((m, i) => ({ month: MONTHS[i].slice(0, 3), count: m.count, volK: Math.round(m.volume / 1000),
      count_prev: pv[i].count, volK_prev: Math.round(pv[i].volume / 1000) }));
    if (month !== "all") monthChart = [monthChart[Number(month) - 1]];
    else if (!data.monthly.length && !(prev?.monthly || []).length) monthChart = [];
  }
  const provvChart = (provv?.months || []).map((m, i) => {
    const p = prevProvv?.months?.[i];
    return { ...m, entrata_prev: p?.entrata || 0, uscita_prev: p?.uscita || 0, margine_prev: p?.margine || 0 };
  });
  const cmp = !!prev;
  const byOwner = cmp ? mergeByName(data.by_owner, prev.by_owner) : data.by_owner;
  const byCanale = cmp ? mergeByName(data.by_canale, prev.by_canale) : data.by_canale;
  const bySede = cmp ? mergeByName(data.by_sede, prev.by_sede) : data.by_sede;
  const prevProd = Object.fromEntries((prev?.by_prodotto || []).map((x) => [x.name, x]));
  const byProdotto = (data.by_prodotto || []).map((x, i) => ({
    ...x, label: `${i + 1}. ${x.name} (${x.count})`, volK: Math.round(x.volume / 1000),
    volume_prev: prevProd[x.name]?.volume || 0, count_prev: prevProd[x.name]?.count || 0,
    volK_prev: Math.round((prevProd[x.name]?.volume || 0) / 1000),
  }));
  const years = Array.from(new Set([...(data.years || []).map(String), CUR_YEAR])).sort();
  const prevSub = (v) => (cmp ? ` · ${prevYear}: ${v}` : "");

  return (
    <div className="space-y-6">
      <ExportDialog open={expOpen} onOpenChange={setExpOpen} title={expType === "sede" ? "Esporta Report per Sede" : "Esporta Report Pratiche"} fields={expType === "sede" ? null : PRACTICE_EXPORT_FIELDS} dateFields={DATE_FIELDS_PRATICHE} onConfirm={onExport} />
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">Benvenuto, {user?.name} — panoramica delle pratiche</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={String(year)} onValueChange={setYear}>
            <SelectTrigger className="w-36" data-testid="dash-filter-year"><SelectValue placeholder="Anno" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli anni</SelectItem>
              {years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={String(month)} onValueChange={setMonth}>
            <SelectTrigger className="w-36" data-testid="dash-filter-month"><SelectValue placeholder="Mese" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i mesi</SelectItem>
              {MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
          <MultiSelect value={prodotto} onValueChange={setProdotto} className="w-52" testid="dash-filter-prodotto"
            allLabel="Tutti i prodotti" options={products.map((p) => ({ value: p, label: p }))} />
          {user?.role === "admin" && (
            <Button data-testid="export-per-sede-btn" onClick={() => { setExpType("sede"); setExpOpen(true); }} variant="outline">
              <Download className="h-4 w-4 mr-2" /> Report per Sede
            </Button>
          )}
          {user?.role === "admin" && (
            <Button data-testid="export-report-excel-btn" onClick={() => { setExpType("report"); setExpOpen(true); }} variant="outline">
              <Download className="h-4 w-4 mr-2" /> Esporta Report Excel
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi testid="kpi-total-volume" icon={Wallet} label="Volume Totale" value={fmtEuro(data.total_volume)} sub={`${data.total_practices} pratiche${prevSub(fmtEuro(prev?.total_volume))}`} tint="bg-primary/10 text-primary" />
        <Kpi testid="kpi-active-practices" icon={Activity} label="In Lavorazione" value={data.active_practices} sub={`pratiche attive${prevSub(prev?.active_practices)}`} tint="bg-indigo-500/10 text-indigo-500" />
        <Kpi testid="kpi-approval-rate" icon={CheckCircle2} label="Tasso Approvazione" value={`${data.approval_rate}%`} sub={`su pratiche decise${prevSub(`${prev?.approval_rate}%`)}`} tint="bg-emerald-500/10 text-emerald-500" />
        <Kpi testid="kpi-liquidated" icon={TrendingUp} label="Volume Liquidato" value={fmtEuro(data.liquidated_volume)} sub={`erogato${prevSub(fmtEuro(prev?.liquidated_volume))}`} tint="bg-teal-500/10 text-teal-500" />
      </div>

      {user?.role === "admin" && provv && (
        <Card className="p-5" data-testid="chart-provvigioni">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2">
              <Wallet className="h-4 w-4 text-primary" />
              <div>
                <h2 className="font-heading font-semibold">Provvigioni Entrata / Uscita {year === "all" ? "· Tutti gli anni" : year}{month !== "all" ? ` · ${MONTHS[Number(month) - 1]}` : ""}</h2>
                <p className="text-xs text-muted-foreground">Volumi netti per mese · margine (delta) evidenziato{prevProvv ? ` · in tenue il ${prevYear}` : ""}</p>
              </div>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={320}>
            <ComposedChart data={provvChart} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
              <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
              <Tooltip formatter={(v, n) => [fmtEuro(v), n]} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {prevProvv && <Area type="monotone" dataKey="entrata_prev" fill="#22c55e" fillOpacity={0.12} stroke="#22c55e" strokeOpacity={0.3} name={`Entrata ${prevYear}`} />}
              {prevProvv && <Area type="monotone" dataKey="uscita_prev" fill="#facc15" fillOpacity={0.15} stroke="#facc15" strokeOpacity={0.35} name={`Uscita ${prevYear}`} />}
              {prevProvv && <Line type="monotone" dataKey="margine_prev" stroke="#ef4444" strokeOpacity={0.3} strokeDasharray="5 4" strokeWidth={2} dot={false} name={`Margine ${prevYear}`} />}
              <Bar dataKey="entrata" fill="#22c55e" radius={[4, 4, 0, 0]} name="Entrata" barSize={18} />
              <Bar dataKey="uscita" fill="#facc15" radius={[4, 4, 0, 0]} name="Uscita" barSize={18} />
              <Line type="monotone" dataKey="margine" stroke="#ef4444" strokeWidth={2.5} dot={{ r: 3 }} name="Margine (delta)" />
            </ComposedChart>
          </ResponsiveContainer>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-5" data-testid="chart-status-distribution">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="h-4 w-4 text-primary" />
            <h2 className="font-heading font-semibold">Distribuzione per Stato</h2>
          </div>
          {statusChart.length ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={statusChart} dataKey="count" nameKey="status" cx="50%" cy="50%" outerRadius={95} label={(e) => e.count}>
                  {statusChart.map((e) => <Cell key={e.status} fill={STATUS_META[e.status]?.dot || "#0ea5e9"} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-muted-foreground py-12 text-center">Nessun dato</p>}
          <div className="flex flex-wrap gap-2 mt-3">
            {statusChart.map((s) => (
              <span key={s.status} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                <span className="h-2 w-2 rounded-full" style={{ background: STATUS_META[s.status]?.dot }} />{s.status} ({s.count})
              </span>
            ))}
          </div>
        </Card>

        <Card className="p-5" data-testid="chart-monthly-trends">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="h-4 w-4 text-primary" />
            <h2 className="font-heading font-semibold">Andamento Mensile (volume €K){cmp ? ` · confronto ${prevYear}` : ""}</h2>
          </div>
          {monthChart.length ? (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={monthChart} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                <Tooltip />
                {cmp && <Legend wrapperStyle={{ fontSize: 12 }} />}
                {cmp && <Line type="monotone" dataKey="volK_prev" stroke="#0ea5e9" strokeOpacity={0.3} strokeDasharray="5 4" strokeWidth={2} dot={false} name={`Volume €K ${prevYear}`} />}
                {cmp && <Line type="monotone" dataKey="count_prev" stroke="#10b981" strokeOpacity={0.3} strokeDasharray="5 4" strokeWidth={2} dot={false} name={`N. Pratiche ${prevYear}`} />}
                <Line type="monotone" dataKey="volK" stroke="#0ea5e9" strokeWidth={2.5} dot={{ r: 4 }} name="Volume €K" />
                <Line type="monotone" dataKey="count" stroke="#10b981" strokeWidth={2.5} dot={{ r: 4 }} name="N. Pratiche" />
              </LineChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-muted-foreground py-12 text-center">Nessun dato</p>}
        </Card>
      </div>

      {isInternal(user?.role) && byOwner.length > 0 && (
        <Card className="p-5" data-testid="chart-by-owner">
          <h2 className="font-heading font-semibold mb-4">Pratiche per Collaboratore</h2>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={byOwner} barGap={-28}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
              <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" allowDecimals={false} />
              <Tooltip />
              {cmp && <Legend wrapperStyle={{ fontSize: 12 }} />}
              {cmp && <Bar dataKey="count_prev" fill="#6366f1" fillOpacity={0.18} radius={[6, 6, 0, 0]} barSize={28} name={`Pratiche ${prevYear}`} />}
              <Bar dataKey="count" fill="#6366f1" radius={[6, 6, 0, 0]} barSize={cmp ? 28 : undefined} name={cmp ? `Pratiche ${year}` : "Pratiche"} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}
      {byProdotto.length > 0 && (
        <Card className="p-5" data-testid="chart-by-prodotto">
          <div className="mb-4">
            <h2 className="font-heading font-semibold">Classifica Prodotti</h2>
            <p className="text-xs text-muted-foreground">Volume (€K) e numero di pratiche per prodotto, dal più venduto{cmp ? ` · in tenue il ${prevYear}` : ""}</p>
          </div>
          <ResponsiveContainer width="100%" height={Math.max(180, byProdotto.length * 44 + 40)}>
            <BarChart data={byProdotto} layout="vertical" margin={{ top: 0, right: 24, left: 8, bottom: 0 }} barGap={-18}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
              <YAxis type="category" dataKey="label" width={170} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
              <Tooltip formatter={(v, n, p) => (n.startsWith("Volume") ? [`${fmtEuro(n.includes(String(prevYear)) ? p.payload.volume_prev : p.payload.volume)} · ${n.includes(String(prevYear)) ? p.payload.count_prev : p.payload.count} pratiche`, n] : [v, n])} />
              {cmp && <Legend wrapperStyle={{ fontSize: 12 }} />}
              {cmp && <Bar dataKey="volK_prev" fill="#8b5cf6" fillOpacity={0.18} radius={[0, 6, 6, 0]} barSize={18} name={`Volume ${prevYear}`} />}
              <Bar dataKey="volK" fill="#8b5cf6" radius={[0, 6, 6, 0]} barSize={18} name={cmp ? `Volume ${year}` : "Volume"}
                label={{ position: "right", fontSize: 11, fill: "hsl(var(--muted-foreground))", formatter: (v) => v }} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      {isInternal(user?.role) && byCanale && byCanale.length > 0 && (
        <Card className="p-5" data-testid="chart-by-canale">
          <h2 className="font-heading font-semibold mb-4">Pratiche per Canale di Provenienza</h2>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={byCanale} barGap={-28}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
              <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" allowDecimals={false} />
              <Tooltip />
              {cmp && <Legend wrapperStyle={{ fontSize: 12 }} />}
              {cmp && <Bar dataKey="count_prev" fill="#f59e0b" fillOpacity={0.18} radius={[6, 6, 0, 0]} barSize={28} name={`Pratiche ${prevYear}`} />}
              <Bar dataKey="count" fill="#f59e0b" radius={[6, 6, 0, 0]} barSize={cmp ? 28 : undefined} name={cmp ? `Pratiche ${year}` : "Pratiche"} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}

      {isInternal(user?.role) && bySede && bySede.length > 0 && (
        <Card className="p-5" data-testid="chart-by-sede">
          <h2 className="font-heading font-semibold mb-4">Pratiche per Sede di Provenienza</h2>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={bySede} barGap={-28}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
              <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" allowDecimals={false} />
              <Tooltip />
              {cmp && <Legend wrapperStyle={{ fontSize: 12 }} />}
              {cmp && <Bar dataKey="count_prev" fill="#10b981" fillOpacity={0.18} radius={[6, 6, 0, 0]} barSize={28} name={`Pratiche ${prevYear}`} />}
              <Bar dataKey="count" fill="#10b981" radius={[6, 6, 0, 0]} barSize={cmp ? 28 : undefined} name={cmp ? `Pratiche ${year}` : "Pratiche"} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      )}
    </div>
  );
}
